import { motion } from 'framer-motion';
import { Edit, Eye, MapPin, Plus, Search, Target, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import './SiteManagement.css';
import { deleteSite, getAllSites, parseGeofenceDetails } from './siteApi';

const SiteList = ({
  onSiteSelect,
  onCreateNew,
  onEditSite,
  searchQuery = '',
  onSearchChange,
  isSidebar = false,
}) => {
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchSites();
  }, []);

  useEffect(() => {
    filterSites();
  }, [searchQuery, internalSearchQuery, sites]);

  const fetchSites = async () => {
    try {
      setLoading(true);
      const sitesData = await getAllSites();
      // Sort sites alphabetically by site_name
      const sortedSites = [...sitesData].sort((a, b) => {
        const nameA = (a.site_name || '').toLowerCase();
        const nameB = (b.site_name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setSites(sortedSites);
      setError(null);
    } catch (err) {
      console.error('Error fetching sites:', err);
      setError('Failed to load sites. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterSites = () => {
    const currentQuery = searchQuery || internalSearchQuery;
    if (!currentQuery.trim()) {
      // Sites are already sorted in fetchSites, so just set them
      setFilteredSites(sites);
      return;
    }

    const filtered = sites.filter(
      site =>
        site.site_name?.toLowerCase().includes(currentQuery.toLowerCase()) ||
        site.address?.toLowerCase().includes(currentQuery.toLowerCase())
    );
    // Sort filtered results alphabetically by site_name
    const sortedFiltered = [...filtered].sort((a, b) => {
      const nameA = (a.site_name || '').toLowerCase();
      const nameB = (b.site_name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    setFilteredSites(sortedFiltered);
  };

  const handleDeleteSite = async (siteId, siteName) => {
    try {
      await deleteSite(siteId);
      setSites(sites.filter(site => site.site_id !== siteId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting site:', err);
      alert('Failed to delete site. Please try again.');
    }
  };

  const getGeofenceDisplay = geofenceDetails => {
    try {
      const details = parseGeofenceDetails(geofenceDetails);

      if (details.type === 'Polygon') {
        return `${details.coordinates?.length || 0} points`;
      } else if (details.type === 'Circle') {
        return `${details.radius || 0}m radius`;
      }
      return 'Unknown type';
    } catch (error) {
      return 'Invalid data';
    }
  };

  const getGeofenceIcon = geofenceDetails => {
    try {
      const details = parseGeofenceDetails(geofenceDetails);
      return details.type === 'Circle' ? (
        <Target className="w-4 h-4" />
      ) : (
        <MapPin className="w-4 h-4" />
      );
    } catch (error) {
      return <MapPin className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="site-list-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading sites...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="site-list-container">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchSites} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`site-list-container ${isSidebar ? 'sidebar-mode' : ''}`}>
      {/* Header - Only show when not in sidebar */}
      {!isSidebar && (
        <div className="site-list-header">
          <h2 className="site-list-title">Configure Location</h2>
          <button onClick={onCreateNew} className="add-site-button">
            <Plus className="w-4 h-4" />
            Add New Site
          </button>
        </div>
      )}

      {/* Search Bar - Only show when not in sidebar */}
      {!isSidebar && (
        <div className="search-container">
          <div className="search-input-wrapper">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Search sites..."
              value={internalSearchQuery}
              onChange={e => setInternalSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      )}

      {/* Sites List */}
      <div className="sites-list">
        {filteredSites.length === 0 ? (
          <div className="no-sites-container">
            <p className="no-sites-message">
              {searchQuery
                ? 'No sites found matching your search.'
                : 'No sites available.'}
            </p>
            {!searchQuery && (
              <button
                onClick={onCreateNew}
                className="create-first-site-button"
              >
                Create Your First Site
              </button>
            )}
          </div>
        ) : (
          filteredSites.map(site => (
            <motion.div
              key={site.site_id}
              className="site-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="site-card-header">
                <h3 className="site-name">
                  {site.site_name || 'Unnamed Site'}
                </h3>
                <div className="site-actions">
                  <button
                    onClick={() => onSiteSelect(site)}
                    className="action-button view-button"
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onEditSite(site)}
                    className="action-button edit-button"
                    title="Edit Site"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(site)}
                    className="action-button delete-button"
                    title="Delete Site"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="site-card-content">
                <div className="site-info-row">
                  <MapPin className="info-icon" />
                  <span className="site-address">
                    {site.address || 'No address provided'}
                  </span>
                </div>

                <div className="site-info-row">
                  {getGeofenceIcon(site.geofence_details)}
                  <span className="geofence-info">
                    {getGeofenceDisplay(site.geofence_details)}
                  </span>
                </div>

                {site.authorised_ids && site.authorised_ids.length > 0 && (
                  <div className="site-info-row">
                    <div className="supervisor-icon">👤</div>
                    <span className="supervisor-info">
                      {site.authorised_ids.length} supervisor(s) assigned
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-confirmation-modal">
            <h3>Delete Site</h3>
            <p>
              Are you sure you want to delete "{deleteConfirm.site_name}"? This
              action cannot be undone and may affect assigned employees and
              shifts.
            </p>
            <div className="modal-actions">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  handleDeleteSite(
                    deleteConfirm.site_id,
                    deleteConfirm.site_name
                  )
                }
                className="delete-confirm-button"
              >
                Yes, Delete Site
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteList;
